import React, { useCallback, useEffect, useState } from 'react';
import api from '../../Services/api';
import { FaCircleInfo } from "react-icons/fa6";
import Navbar from './NavBar';
import Footer from './Footer';
import IndustriesDataTable from '../../Component/IndustriesDataTable';
import { Autocomplete, TextField, Tooltip, Box, Typography } from '@mui/material';

const MinWages = () => {
  const currentYear = new Date().getFullYear()
  const [selectedState, setSelectedState] = useState('')
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedIndustry, setSelectedIndustry] = useState('')
  const [allStates, setAllStates] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [industryTypes, setIndustryTypes] = useState([])
  const [loading, setLoading] = useState(true)

  const handleGetIndustriesDetails = React.useCallback(async () => {
    if (!selectedState || !selectedYear) return;

    setFilteredData([]);
    setLoading(true);

    try {
      const response = await api.minimumWages.getMinimumWagesByStateYearIndustry(
        selectedState, selectedYear, selectedIndustry
      );
      if (response.status === 200) {
        setFilteredData(response.data.data);
      } else {
        setFilteredData([]);
      }
    } catch {
      setFilteredData([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }

  }, [selectedState, selectedYear, selectedIndustry]);


  useEffect(() => {
    const handleGetAllStates = async () => {
      try {
        const response = await api.common.getAllStatesApi()
        if (response?.data?.states) {
          const sortedStates = response.data.states.sort((a, b) =>
            a.State.localeCompare(b.State)
          )
          setAllStates(sortedStates)
          setSelectedState(sortedStates[0]?.StateId)
        }
      } catch {
        setAllStates([])
      }
    }
    handleGetAllStates()
  }, [])


  const handleGetIndustryTypesBasedOnState = useCallback(async () => {
    if (!selectedState) return
    const response = await api.minimumWages.onGetIndustryTypesBasedOnState(selectedState)
    if (response.status === 200) {
      const { data } = response.data
      setIndustryTypes(data)
      setSelectedIndustry(data[0].industryType)
    } else {
      setIndustryTypes([])
      setSelectedIndustry('')
    }
  }, [selectedState]);

  useEffect(() => {
    handleGetIndustryTypesBasedOnState()
  }, [handleGetIndustryTypesBasedOnState])

  useEffect(() => {
    handleGetIndustriesDetails()
  }, [handleGetIndustriesDetails])

  return (
    <>
      <Navbar />
      <div className='w-full h-full overflow-auto pt-20'>
        <section className='flex flex-col  lg:flex-row justify-between px-6 py-4 lg:px-8 lg:py-6 bg-white shadow-card-shadow-three'>
          <div className='w-fit flex flex-col gap-2'>
            <h1 className='text-2xl lg:text-3xl font-extrabold'>Minimum Wages</h1>
          </div>
        </section>
        <div className='lg:px-5 lg:py-3 max-w-7xl mx-auto px-6 py-4'>
          <section className='bg-white p-6 pb-8 space-y-5 border-2 rounded-xl'>
            <h1 className='text-2xl font-semibold tracking-tight'>Filter Wage Data</h1>
            <div className='grid grid-cols-3 gap-4 lg:gap-20'>
              <div className='flex flex-col'>
                <label htmlFor="selectState" className="mb-1 font-medium text-gray-700">
                  State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="p-2 border rounded-lg w-full max-w-xs"
                >
                  <option value="" disabled>Select State</option>
                  {allStates.map((state) => (
                    <option key={state.StateId} value={state.StateId}>
                      {state.State}
                    </option>
                  ))}
                </select>
              </div>

              <div className='flex flex-col'>
                <label htmlFor="selectState" className="mb-1 font-medium text-gray-700">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => { setSelectedYear(e.target.value) }}
                  className="p-2 border rounded-lg w-full max-w-xs"
                >
                  <option value="" disabled>Select Year</option>
                  {[
                    (currentYear - 1).toString(),
                    currentYear.toString(),
                    (currentYear + 1).toString()
                  ].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div className='flex flex-col'>
                <label htmlFor="selectState" className="mb-1 font-medium text-gray-700">
                  Industry
                </label>
                <Autocomplete
                  options={industryTypes}
                  getOptionLabel={(option) => option.industryType}
                  value={industryTypes.find(i => i.industryType === selectedIndustry) || null}
                  onChange={(e, val) => setSelectedIndustry(val?.industryType || '')}
                  filterOptions={(options, state) => {
                    const text = state.inputValue.trim().toLowerCase();
                    if (text === '') return options;
                    if (text.length < 3) return [];
                    return options.filter((o) =>
                      o.industryType.toLowerCase().includes(text)
                    );
                  }}
                  openOnFocus={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Type to search (min 3 letters)"
                      size="small"
                    />
                  )}
                  renderOption={(props, option, { inputValue }) => {
                    const text = option.industryType;
                    const searchTerm = inputValue.trim();
                    const regex = new RegExp(`(${searchTerm})`, 'gi');
                    const parts = text.split(regex);
                    const highlightedText = parts.map((part, i) =>
                      part.toLowerCase() === searchTerm.toLowerCase() ? (
                        <span key={i} className="bg-yellow-300 font-semibold">
                          {part}
                        </span>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    );

                    const isLong = text.length > 30;

                    return (
                      <li {...props}>
                        {isLong ? (
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  maxWidth: 300,
                                  maxHeight: 150,
                                  overflowY: 'auto',
                                  whiteSpace: 'normal',
                                  p: 1,
                                  bgcolor: 'white',
                                  color: 'black',
                                  borderRadius: 1,
                                  boxShadow: 3,
                                }}
                              >
                                <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
                                  {highlightedText}
                                </Typography>
                              </Box>
                            }
                            placement="right"
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'transparent',
                                  p: 0,
                                },
                              },
                            }}
                          >
                            <span
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                maxWidth: '250px',
                              }}
                            >
                              {highlightedText}
                            </span>
                          </Tooltip>
                        ) : (
                          <span
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                              maxWidth: '250px',
                            }}
                          >
                            {highlightedText}
                          </span>
                        )}
                      </li>
                    );
                  }}
                  fullWidth
                  size="small"
                />
              </div>
            </div>
          </section>
          {
            loading ? (
              <div className="d-flex flex-row align-items-center justify-content-center" style={{ marginTop: '80px' }}>
                <h3>Loading</h3>
                <div className="spinner-grow text-danger mx-2" style={{ width: '1rem', height: '1rem' }}></div>
                <div className="spinner-grow text-danger mx-2" style={{ width: '1rem', height: '1rem' }}></div>
                <div className="spinner-grow text-danger mx-2" style={{ width: '1rem', height: '1rem' }}></div>
              </div>
            ) : (
              selectedState && selectedYear ? (
                filteredData.length > 0 ? (
                  <IndustriesDataTable data={filteredData} />
                ) : (
                  <div className="flex justify-center mt-10 w-full shadow-xl">
                    <div className="bg-white border border-yellow-400 text-primary-color rounded-xl p-6 w-full text-center shadow-sm">
                      <div className='flex justify-center items-center'><FaCircleInfo className='lg:size-8' /></div>
                      <div className="text-xl font-bold mb-2">No Data Found</div>
                      <div className="text-sm mb-4">
                        Sorry, there is no wage data available for the selected state, industry, or year.
                      </div>
                    </div>
                  </div>
                )
              ) : null
            )
          }


        </div>
      </div>
      <Footer />
    </>
  );
};

export default MinWages;
